import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { gradingStrategiesApi } from '@/api/config-governance';
import {
  GRADING_MODELS,
  GRADING_SCALES,
  type GradingModel,
  type GradingScale,
  type GradingStrategy,
} from '@/types/config-governance';
import styles from './platform-pages.module.css';
import gov from './governance.module.css';

/**
 * CFG-13 — the grading strategy register: every ruler marks are read against,
 * platform catalogue and per-institution variants alike.
 *
 * The scope filter mirrors the controller's convention directly: the literal
 * 'platform' means catalogue entries (institutionId IS NULL), a UUID means one
 * institution's own shelf, nothing means everything. Rows group by code
 * because versions of one code are one story — v3 ACTIVE with v4 DRAFT beside
 * it is the normal shape of a strategy being revised.
 *
 * Creation seeds two bands split at the pass threshold rather than posting an
 * empty shell: the DTO demands at least one band, and a draft that starts
 * valid teaches the band editor by example instead of opening on a screen of
 * errors.
 */

const STATUS_FILTERS = ['', 'DRAFT', 'ACTIVE', 'SUPERSEDED'] as const;

type ScopeChoice = 'all' | 'platform' | 'institution';

export function GradingStrategiesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const scopeParam = params.get('scope') ?? '';
  // The scope parameter is 'platform', an institution UUID, or — as the
  // "institution chosen, none picked yet" placeholder — a single space, which
  // trims to nothing and therefore never reaches the wire.
  const scope: ScopeChoice =
    scopeParam === 'platform' ? 'platform' : scopeParam ? 'institution' : 'all';
  const scopeInstitution = scope === 'institution' ? scopeParam.trim() : '';
  const [creating, setCreating] = useState(false);

  const fetcher = useCallback(() => {
    return gradingStrategiesApi.list({
      ...(status ? { status } : {}),
      ...(scope === 'platform'
        ? { institutionId: 'platform' }
        : scope === 'institution' && scopeInstitution
          ? { institutionId: scopeInstitution }
          : {}),
    });
  }, [status, scope, scopeInstitution]);

  const { state, reload } = useAsyncData<GradingStrategy[]>(
    `grading-strategies:${status}:${scopeParam}`,
    fetcher,
  );

  const setFilters = (nextStatus: string, nextScope: string) => {
    const next = new URLSearchParams();
    if (nextStatus) next.set('status', nextStatus);
    if (nextScope) next.set('scope', nextScope);
    setParams(next);
  };

  const groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    const byCode = new Map<string, GradingStrategy[]>();
    for (const strategy of state.data) {
      // Same code in two scopes is two families — a school's own
      // TWENTY_POINT_SCALE must not fold into the catalogue's.
      const key = `${strategy.code}::${strategy.institutionId ?? 'platform'}`;
      const list = byCode.get(key) ?? [];
      list.push(strategy);
      byCode.set(key, list);
    }
    return [...byCode.values()];
  }, [state]);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('grading.title')}</h1>
          <p className={styles.pageLead}>{t('grading.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          {t('grading.newStrategy')}
        </button>
      </header>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="grading-status">
            {t('grading.filterStatus')}
          </label>
          <Select
            id="grading-status"
            value={status}
            onChange={(next) => setFilters(next, scopeParam)}
            options={STATUS_FILTERS.map((value) => ({
              value,
              label: value ? t(`platform.state.lifecycle.${value}`) : t('grading.filterAllStatuses'),
            }))}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="grading-scope">
            {t('grading.filterScope')}
          </label>
          <Select
            id="grading-scope"
            value={scope}
            onChange={(next) =>
              setFilters(status, next === 'all' ? '' : next === 'platform' ? 'platform' : ' ')
            }
            options={[
              { value: 'all', label: t('grading.scopeAll') },
              { value: 'platform', label: t('grading.scopePlatform') },
              { value: 'institution', label: t('grading.scopeInstitution') },
            ]}
          />
        </div>
        {scope === 'institution' && (
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="grading-institution">
              {t('grading.institutionLabel')}
            </label>
            <InstitutionPicker
              id="grading-institution"
              value={scopeInstitution}
              onChange={(next) => setFilters(status, next || ' ')}
            />
          </div>
        )}
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('grading.emptyTitle')}
        emptyBody={t('grading.emptyBody')}
      >
        {() => (
          <ul className={styles.rowList}>
            {groups.map((family) =>
              family.map((strategy, index) => (
                <li key={strategy.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <Link
                      className={styles.rowTitleLink}
                      to={`/admin/grading-strategies/${encodeURIComponent(strategy.id)}`}
                    >
                      {strategy.name}
                    </Link>
                    <div className={styles.rowMetaLine}>
                      {index === 0 && <code className={styles.codeTag}>{strategy.code}</code>}
                      <span className={styles.mutedTag}>
                        {t('grading.versionShort', { n: strategy.version })}
                      </span>
                      <StateBadge kind="lifecycle" value={strategy.status} size="sm" />
                      <span className={gov.scopeTag}>
                        {strategy.institutionId === null
                          ? t('grading.scopePlatform')
                          : t('grading.scopeInstitution')}
                      </span>
                      <span className={styles.infoTag}>
                        {t(`grading.scales.${strategy.gradingScale}`, {
                          defaultValue: strategy.gradingScale,
                        })}
                      </span>
                    </div>
                    {strategy.description && (
                      <p className={styles.rowDetail}>{strategy.description}</p>
                    )}
                  </div>
                  <div className={styles.rowAside}>
                    <span className={styles.mutedCell}>
                      {t('grading.bandCount', { total: strategy.bands.length })}
                    </span>
                    <span className={styles.mutedCell}>
                      {t('grading.rangeSummary', {
                        min: strategy.minScore,
                        max: strategy.maxScore,
                        threshold: strategy.passThreshold,
                      })}
                    </span>
                  </div>
                </li>
              )),
            )}
          </ul>
        )}
      </AsyncPanel>

      <CreateStrategyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          toast.success(t('grading.createdToast', { code: created.code, n: created.version }));
          navigate(`/admin/grading-strategies/${encodeURIComponent(created.id)}`);
        }}
      />
    </div>
  );
}

function CreateStrategyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: GradingStrategy) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [gradingModel, setGradingModel] = useState<GradingModel>('SEQUENCE_TERM_ANNUAL');
  const [gradingScale, setGradingScale] = useState<GradingScale>('TWENTY_POINT_SCALE');
  const [minScore, setMinScore] = useState('0');
  const [maxScore, setMaxScore] = useState('20');
  const [passThreshold, setPassThreshold] = useState('10');
  const [institutionId, setInstitutionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeValid = /^[A-Z][A-Z0-9_]*$/.test(code);
  const min = Number(minScore);
  const max = Number(maxScore);
  const threshold = Number(passThreshold);
  const numbersValid = [min, max, threshold].every(Number.isFinite) && min < max;
  const canSubmit = codeValid && name.trim().length > 0 && numbersValid && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Two seeded bands split at the threshold, in the stepped convention
      // (fail ends one step below the threshold at two decimals). The draft
      // opens already valid; the editor is for shaping it further.
      const step = 0.01;
      const failTop = Math.max(min, Math.round((threshold - step) * 100) / 100);
      const created = await gradingStrategiesApi.create({
        code,
        name: name.trim(),
        gradingModel,
        gradingScale,
        minScore: min,
        maxScore: max,
        passThreshold: threshold,
        decimalPlaces: 2,
        bands:
          threshold > min && threshold < max
            ? [
                { code: 'FAIL', label: t('grading.seedFailLabel'), minInclusive: min, maxInclusive: failTop },
                { code: 'PASS', label: t('grading.seedPassLabel'), minInclusive: threshold, maxInclusive: max },
              ]
            : [{ code: 'ALL', label: t('grading.seedAllLabel'), minInclusive: min, maxInclusive: max }],
        ...(institutionId ? { institutionId } : {}),
      });
      onCreated(created);
    } catch (cause) {
      // The server's sentence, verbatim — it names the existing draft when
      // the refusal is "a DRAFT of this code already exists in this scope".
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('grading.createTitle')}
      description={t('grading.createIntro')}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-strategy-code">
            {t('grading.fieldCode')}
          </label>
          <p className={styles.fieldHint}>{t('grading.fieldCodeHint')}</p>
          <input
            id="new-strategy-code"
            className={gov.editorInput}
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            aria-invalid={code.length > 0 && !codeValid}
            spellCheck={false}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-strategy-name">
            {t('grading.fieldName')}
          </label>
          <input
            id="new-strategy-name"
            className={gov.editorInput}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-strategy-model">
              {t('grading.fieldModel')}
            </label>
            <Select
              id="new-strategy-model"
              value={gradingModel}
              onChange={(next) => setGradingModel(next as GradingModel)}
              options={GRADING_MODELS.map((value) => ({
                value,
                label: t(`grading.models.${value}`, { defaultValue: value }),
              }))}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-strategy-scale">
              {t('grading.fieldScale')}
            </label>
            <Select
              id="new-strategy-scale"
              value={gradingScale}
              onChange={(next) => setGradingScale(next as GradingScale)}
              options={GRADING_SCALES.map((value) => ({
                value,
                label: t(`grading.scales.${value}`, { defaultValue: value }),
              }))}
            />
          </div>
        </div>
        <div className={styles.dialogGrid}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-strategy-min">
              {t('grading.fieldMin')}
            </label>
            <input
              id="new-strategy-min"
              className={gov.editorInputNarrow}
              inputMode="decimal"
              value={minScore}
              onChange={(event) => setMinScore(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-strategy-max">
              {t('grading.fieldMax')}
            </label>
            <input
              id="new-strategy-max"
              className={gov.editorInputNarrow}
              inputMode="decimal"
              value={maxScore}
              onChange={(event) => setMaxScore(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-strategy-threshold">
              {t('grading.fieldThreshold')}
            </label>
            <input
              id="new-strategy-threshold"
              className={gov.editorInputNarrow}
              inputMode="decimal"
              value={passThreshold}
              onChange={(event) => setPassThreshold(event.target.value)}
            />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-strategy-institution">
            {t('grading.fieldScope')}
          </label>
          <p className={styles.fieldHint}>{t('grading.fieldScopeHint')}</p>
          <InstitutionPicker
            id="new-strategy-institution"
            value={institutionId}
            clearable
            onChange={setInstitutionId}
            placeholder={t('grading.scopePlatform')}
          />
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="modal-button" disabled={!canSubmit} onClick={submit}>
            {submitting ? t('governance.transition.working') : t('grading.createConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
